/**
 * The environment — a gym-style wrapper around `World`.
 *
 * `reset(seed) -> observations`, `step(actions) -> { observations, rewards,
 * done, info }`. Nothing else. Everything that makes this a learning problem
 * rather than a simulation lives in the three siblings: ./obs builds the input
 * vector, ./actions maps an index to controls, ./reward scores the outcome.
 *
 * ===========================================================================
 * FRAME SKIP: 8 TICKS
 * ===========================================================================
 *
 * The simulation runs at 120 Hz. A policy that acts every tick spends 120
 * forward passes per simulated second to make decisions that differ by 8 ms,
 * and — worse for learning — stretches the credit-assignment horizon by 8x, so
 * the discount has to be pushed toward 1 to see the same amount of future.
 *
 * `DEFAULT_FRAME_SKIP = 8` holds each action for 8 ticks = 1/15 s = 66.7 ms,
 * which is:
 *   - RLGym's default `tick_skip`, so published hyperparameters transfer;
 *   - roughly a human's controller resolution, so nothing playable is lost;
 *   - short enough that a flip (0.65 s of torque) still spans several actions.
 *
 * The action is held, not re-sampled: the learner's `CarInput` is chosen once
 * and the same frozen object is handed to all 8 ticks. SCRIPTED cars are not
 * frame-skipped — they run their full-rate control loop every tick, because a
 * bot at 15 Hz is a different and much weaker opponent than the one the rest of
 * the project tests.
 *
 * ===========================================================================
 * EPISODES
 * ===========================================================================
 *
 * An episode ends on the first goal, or after `episodeTicks`. It never ends on
 * the match clock: the match config is built with the countdown removed and a
 * regulation length far longer than any episode, so tick 0 is already live play
 * and the world's own phase machine never interrupts a rollout.
 *
 * INITIAL STATES ARE RANDOMIZED. A policy trained only from kickoff sees a
 * vanishing fraction of the states it will actually be in — it never learns to
 * chase a loose ball, defend a shot, or recover from a bad landing, because it
 * has never been in one of those states without having caused it. `resetMode`
 * selects kickoffs (with the spawn assignment shuffled), fully random ball and
 * car placements, or a mix.
 *
 * ===========================================================================
 * THE CURRICULUM, AND WHY IT IS NOT OPTIONAL
 * ===========================================================================
 *
 * From `kickoff` or `random` the ball starts thousands of uu away, and an
 * untrained policy sampling uniformly from 62 actions essentially never reaches
 * it. `ballTouch` never fires, the goal terms never fire, and the only live
 * signal is the small `playerToBallVelocity` term — which is itself buried
 * under `ballToOpponentGoalDistance`, whose value on those resets is dominated
 * by the ball's own randomized velocity and is therefore pure NOISE from the
 * agent's point of view. That is the whole of why end-to-end runs did not
 * visibly learn: not a missing gradient, an unfavourable signal-to-noise ratio.
 *
 * `nearBall` spawns the learner a short distance from a stationary ball,
 * pointing roughly at it, so contact happens by accident within the first
 * episodes and every downstream term has something to grade. `curriculum`
 * anneals from that toward `mixed` as a difficulty parameter in [0, 1] rises:
 *
 *   difficulty 0   ball at rest in front of one net, the attacking learner
 *                  ~500 uu behind it and the defending one ~500 uu goalside,
 *                  both facing it
 *   difficulty 1   exactly `mixed` — the schedule ends where the old default
 *                  began, so nothing is lost by finishing it
 *
 * The difficulty is set from OUTSIDE (see `setDifficulty`), by the trainer,
 * from the measured touch rate. The environment holds no schedule of its own:
 * a reset must stay a pure function of (seed, difficulty) or the determinism
 * guarantee below stops holding.
 *
 * ===========================================================================
 * DETERMINISM
 * ===========================================================================
 *
 * No `Math.random`, no clock. Every stochastic choice — the reset variant, the
 * placements, the scripted opponents' seeds — is drawn from one `Rng` seeded by
 * `reset(seed)`. Same seed plus same action sequence gives bit-identical
 * observations and rewards; `test/rl/env.test.ts` runs 1000 steps of that.
 */

import { GOAL_CENTER, KICKOFF_SPAWNS, type Spawn } from '../physics/arena-data';
import type { CarInput, CarState } from '../physics/car-state';
import { neutralInput } from '../physics/car-state';
import { ARENA, BOOST_MAX, CAR_SPAWN_REST_Z } from '../physics/constants';
import { clone, vec3 } from '../physics/math';
import type { MutatorConfig } from '../physics/mutators';
import { Rng } from '../physics/rng';
import type { ArenaGeometry } from '../physics/surface';
import { DEFAULT_MATCH_CONFIG, type MatchSnapshot } from '../game/match';
import { BotDirector, viewOf, type BotConfig, type BotWorldView } from '../bot/bot';
import type { ScriptedTier } from '../bot/difficulty';
import { resetCarToSpawn } from '../sim/kickoff';
import { World, type WorldStepEvents } from '../sim/world';
import { actionInput, nearestAction } from './actions';
import { buildObservation, makeObservation } from './obs';
import {
  emptyRewardBreakdown,
  RewardFunction,
  type RewardCarSnapshot,
  type RewardTermName,
  type RlRewardContext,
  type WindowDemolition,
  type WindowGoal,
} from './reward';
import type { ActionId, Observation } from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Ticks each action is held. See the header for why 8. */
export const DEFAULT_FRAME_SKIP = 8;

/** 20 seconds. Long enough for a full possession-and-shot sequence, short
 *  enough that a batch contains many independent initial states. */
export const DEFAULT_EPISODE_TICKS = 2400;

/**
 * How a car is driven.
 *
 *   learner  — the caller supplies an action id; the car gets an observation
 *              and a reward, and counts toward the episode return.
 *   scripted — driven by the project's own `ScriptedPolicy` at a tier. This is
 *              both the opponent for league play and the reference for the
 *              reward-function sanity test.
 *   idle     — neutral input forever. Useful for isolating one agent.
 */
export type AgentController =
  | { readonly kind: 'learner' }
  | { readonly kind: 'scripted'; readonly tier: ScriptedTier; readonly seed?: number }
  | { readonly kind: 'idle' };

export type ResetMode =
  /** Standard kickoff, with the spawn assignment shuffled per team. */
  | 'kickoff'
  /** Random legal ball and car placements across the whole field. */
  | 'random'
  /** Half kickoffs, half random. */
  | 'mixed'
  /** Learners spawned beside the ball, scaled by `difficulty`. */
  | 'nearBall'
  /** `nearBall` annealing into `mixed` as `difficulty` rises. The default. */
  | 'curriculum';

/** Every legal `ResetMode`, for a CLI or a menu that has to validate one. */
export const RESET_MODES: readonly ResetMode[] = [
  'kickoff',
  'random',
  'mixed',
  'nearBall',
  'curriculum',
];

export const isResetMode = (value: unknown): value is ResetMode =>
  typeof value === 'string' && (RESET_MODES as readonly string[]).includes(value);

export interface EnvOptions {
  /** Cars per team. Default 1. */
  teamSize?: number;
  /**
   * One entry per car, parallel to `World.cars` (blue first). Defaults to car 0
   * learning and every other car idle — the least surprising configuration, and
   * the one that isolates a single policy.
   */
  controllers?: readonly AgentController[];
  frameSkip?: number;
  episodeTicks?: number;
  resetMode?: ResetMode;
  /**
   * Curriculum position in [0, 1], for `nearBall` and `curriculum`. 0 is the
   * easiest state distribution, 1 is the full-difficulty one. Ignored by the
   * three fixed modes. Defaults to 1, so an environment constructed without
   * mentioning it behaves at full difficulty whatever mode it is in.
   */
  difficulty?: number;
  rewardWeights?: Partial<Record<RewardTermName, number>>;
  /**
   * When set, the env also runs a scripted bot for every LEARNER car and
   * exposes its choice through `referenceActions()`. The world is still driven
   * by the actions passed to `step`; this is a reference signal, not a
   * controller. It is what makes "a competent player out-scores noise" a test
   * of the reward function.
   */
  referenceTier?: ScriptedTier;
  mutators?: MutatorConfig;
  /** Defaults to the memoized soccar arena. */
  arena?: ArenaGeometry;
}

export type EndReason = 'goal' | 'timeout';

export interface EnvInfo {
  /** Ticks elapsed in the episode, after this step. */
  episodeTick: number;
  /** Ticks this step actually advanced. Equals the frame skip except on the
   *  step that ends the episode. */
  ticks: number;
  terminated: EndReason | null;
  /** Per agent, in `agentCarIndices` order. */
  goalsFor: number[];
  goalsAgainst: number[];
  touched: boolean[];
  /** Per agent: each term's WEIGHTED contribution this step. */
  rewardTerms: Record<string, number>[];
}

export interface StepResult {
  observations: Observation[];
  rewards: number[];
  done: boolean;
  info: EnvInfo;
}

// ---------------------------------------------------------------------------
// Randomized placement
// ---------------------------------------------------------------------------

/** Keep-out radius between any two placed entities at reset, in uu. Two cars
 *  spawned inside each other resolve with a violent depenetration that has
 *  nothing to do with anything the policy did. */
const MIN_SEPARATION = 400;

/** Rejection-sampling budget. Bounded rather than a `while`: an env reset must
 *  cost a fixed amount of work however unlucky the draw. */
const SAMPLE_ATTEMPTS = 32;

/** How far inside the 45-degree corner planes a random placement stays. */
const CORNER_MARGIN = 700;

const RANDOM_CAR_HALF_X = 3600;
const RANDOM_CAR_HALF_Y = 4600;
const RANDOM_BALL_HALF_X = 3400;
const RANDOM_BALL_HALF_Y = 4400;
const RANDOM_BALL_MAX_Z = 1200;
const RANDOM_BALL_MAX_SPEED = 1600;
const RANDOM_BALL_MAX_SPIN = 3;
const RANDOM_CAR_MIN_SPEED = -200;
const RANDOM_CAR_MAX_SPEED = 1600;

// --- the curriculum's placement envelope ------------------------------------

/**
 * How far a learner spawns from the ball at difficulty 0 and at difficulty 1.
 *
 * 500 uu is a little over two car lengths: far enough that the spawn does not
 * resolve as a depenetration against the ball, close enough that a UNIFORMLY
 * RANDOM action sequence reaches it inside a second. That is the property the
 * whole curriculum rests on — the first touches have to happen by accident,
 * because until one does there is nothing for the touch and goal terms to
 * grade.
 *
 * 3500 is roughly the mean car-to-ball distance of a `random` reset, so the top
 * of the ramp is not a discontinuity into the full-difficulty distribution.
 */
const NEAR_BALL_MIN_RADIUS = 500;
const NEAR_BALL_MAX_RADIUS = 3500;

/** Heading error against "pointing at the ball", at difficulty 0 and 1. Pi is
 *  a uniformly random heading, i.e. exactly what `random` does. */
const NEAR_BALL_MIN_YAW_ERROR = Math.PI / 6;
const NEAR_BALL_MAX_YAW_ERROR = Math.PI;

/** Fraction of the full random-placement half-extent the ball may sit in at
 *  difficulty 0. Keeping the easy ball near the middle keeps it away from the
 *  walls, where a bounce is worth more to the reward than anything the policy
 *  did. */
const NEAR_BALL_MIN_FIELD_FRACTION = 0.35;

// --- the scoring chance -----------------------------------------------------

/**
 * WHERE THE EASY BALL SITS, AND WHY IT IS NOT THE MIDDLE OF THE FIELD.
 *
 * The curriculum used to put the difficulty-0 ball inside a box 35% of the
 * field's half-extents — i.e. always around the halfway line — and the learner
 * on a UNIFORMLY random ring around it. Both halves of that are wrong for the
 * one thing the agent has to discover:
 *
 *   - from the halfway line the target goal is 3000-7000 uu away, so even a
 *     perfectly struck touch does not become a goal inside the episode;
 *   - a uniform ring puts the car on the goal side of the ball as often as
 *     behind it, so half of all first touches go the wrong way by construction.
 *
 * The consequence was measured, not guessed: over the last ~1200 iterations of a
 * 12.3M-step run, self-play scored 0.000 goals for and 0.000 against. The
 * `goalScored` term never fired once, so nothing downstream of it could have
 * taught scoring — an agent only learns from outcomes it has actually seen.
 *
 * So at difficulty 0 the ball is placed in front of the attacking team's target
 * goal, and that team's learners behind it. 3400 uu leaves 1720 uu to the goal
 * line — a couple of seconds of ball travel — and a half-width of 900 keeps the
 * shot inside the posts (`GOAL.centerToPost` is 892.755) without pinning it dead
 * centre.
 *
 * All of it anneals to the old distribution at difficulty 1, so the top of the
 * ramp is still `mixed` and the hard end is unchanged.
 */
const NEAR_BALL_SHOOTING_Y = 3400;
const NEAR_BALL_SHOOTING_HALF_X = 900;

/**
 * How far around the ball a learner may spawn from its own ideal side, at
 * difficulty 0 and at difficulty 1. Pi is the uniform ring the placement used to
 * draw unconditionally.
 *
 * For the ATTACKER the arc is centred on the line running out of the target goal
 * through the ball, so at the easy end the car sits between the ball and its OWN
 * goal — the side from which driving forward is a shot. Together with
 * `NEAR_BALL_MIN_YAW_ERROR` this makes "drive at the ball", the one thing the old
 * policy learned, and "shoot at the goal", the thing it never did, the same
 * action.
 *
 * WHY THE DEFENDER IS AIMED TOO. The first version of this aimed only the
 * attacking team and left every other learner on the uniform ring, so at
 * difficulty 0 exactly one of the two cars was in a state anything could be
 * learned from. Measured over 400 resets of 1v1 self-play at difficulty 0, the
 * defender landed goalside of the ball on 0.585 of them — a coin toss, which is
 * what a uniform ring is. Two consequences:
 *
 *   - self-play's easy end is not a duel. Half the time the defender is behind
 *     the ball alongside the attacker, so the episode grades two cars shooting
 *     at the same empty net and nothing about defending it;
 *   - the defender was also in the attacker's way. Its ring is drawn first, and
 *     when it landed inside the attacker's narrow arc the attacker's rejection
 *     sampler ran out of attempts and fell back to a uniform field placement.
 *     Mean attacker distance to the ball came out 654 uu against the 500 the
 *     ring asks for, and 0.5% of resets put the attacker on the WRONG side.
 *
 * So the defender's arc is centred GOALSIDE — the bearing from the ball toward
 * the defender's own net, i.e. the attacker's arc rotated by pi — which puts it
 * between the ball and the goal being shot at. Driving forward from there is a
 * clearance, so both learners have the same first lesson pointing in opposite
 * directions, which is what makes the easy end a duel rather than a drill.
 *
 * Re-measured the same way afterwards: both cars on their intended side on
 * 1.000 of resets, attacker distance exactly 500 uu, and the per-agent touch
 * rate under uniformly random actions 0.407 attacking and 0.410 defending
 * against the old 0.372 and 0.340. The cost is that the easy end is genuinely
 * contested — random-action goals fell from 0.195 per episode to 0.115, and
 * scripted All-Star goals from 0.352 to 0.220 — which is the point rather than a
 * regression: a goal that has to beat a defender is the one worth learning.
 *
 * The defender is drawn on the SAME ring as the attacker rather than deeper.
 * Rings of 1.6x and 2.4x the attacker's were measured and both made it worse on
 * the metric that matters first: the defender's random-action touch rate fell to
 * 0.263 and 0.172, and the episode rate with it, in exchange for a goal rate
 * that rose only to 0.142 and 0.155. They also move the difficulty-1 draw, which
 * the equal ring leaves bit-identical.
 */
const NEAR_BALL_MIN_APPROACH_ERROR = Math.PI / 6;
const NEAR_BALL_MAX_APPROACH_ERROR = Math.PI;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Fisher-Yates on 0..n-1, from the env's generator. */
const shuffledIndices = (rng: Rng, n: number): number[] => {
  const out: number[] = [];
  for (let i = 0; i < n; i++) out.push(i);
  for (let i = n - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const t = out[i];
    out[i] = out[j];
    out[j] = t;
  }
  return out;
};

/** A point inside the arena's floor plan, respecting the corner cuts. */
const samplePlanar = (rng: Rng, halfX: number, halfY: number): { x: number; y: number } => {
  const limit = ARENA.cornerPlaneIntercept - CORNER_MARGIN;
  for (let attempt = 0; attempt < SAMPLE_ATTEMPTS; attempt++) {
    const x = rng.nextRange(-halfX, halfX);
    const y = rng.nextRange(-halfY, halfY);
    if (Math.abs(x) + Math.abs(y) <= limit) return { x, y };
  }
  // Every draw landed in a corner. Fall back to the centre circle, which is
  // always legal, rather than looping forever.
  return { x: rng.nextRange(-1000, 1000), y: rng.nextRange(-1000, 1000) };
};

// ---------------------------------------------------------------------------
// The environment
// ---------------------------------------------------------------------------

export class RLEnv {
  readonly world: World;
  readonly frameSkip: number;
  readonly episodeTicks: number;
  readonly resetMode: ResetMode;
  readonly controllers: readonly AgentController[];
  /** Indices into `world.cars` of the learning agents, ascending. */
  readonly agentCarIndices: readonly number[];
  readonly reward: RewardFunction;

  private readonly rng = new Rng(0);
  private readonly view: BotWorldView;
  /** The match as the world was built, restored on every reset. */
  private readonly pristineMatch: MatchSnapshot;
  /** Car object -> index, for turning event payloads back into indices. */
  private readonly carIndexOf: Map<CarState, number>;

  private readonly inputs: CarInput[];
  private readonly idleInput: Readonly<CarInput> = Object.freeze(neutralInput());

  private readonly scriptedIndices: readonly number[];
  private readonly referenceTier: ScriptedTier | null;
  private director: BotDirector | null = null;
  private refDirector: BotDirector | null = null;
  private refInputs: CarInput[] | null = null;
  private refInputsTick = -1;

  private episodeTickCount = 0;
  private finished = true;
  private episodeSeed = 0;
  /** Curriculum position. Mutable, unlike `resetMode`: the trainer moves it
   *  between iterations, and rebuilding an `RLEnv` to do that would rebuild the
   *  arena mesh and its BVH every time. */
  private difficultyValue: number;

  /** Window accumulators, reused. Nothing here survives a step. */
  private readonly window: {
    ticks: number;
    goals: WindowGoal[];
    demolitions: WindowDemolition[];
    touched: boolean[];
    boostGained: number[];
    lastTouch: number | null;
  };
  /** Start-of-window state, reused; the reward terms only read it. */
  private readonly snapshot: {
    ballPosition: { x: number; y: number; z: number };
    ballVelocity: { x: number; y: number; z: number };
    cars: RewardCarSnapshot[];
  };
  /** Did each car touch the ball in the PREVIOUS window? */
  private previousTouched: boolean[];

  constructor(options: EnvOptions = {}) {
    const teamSize = options.teamSize ?? 1;
    this.world = new World({
      arena: options.arena,
      teamSize,
      // No countdown: tick 0 is live play, so an episode never spends its first
      // 360 ticks frozen at the kickoff pose. The regulation clock is left at
      // its default, which is far longer than any episode.
      matchConfig: { ...DEFAULT_MATCH_CONFIG, countdownDuration: 0 },
      mutators: options.mutators,
    });

    const carCount = this.world.cars.length;
    const controllers =
      options.controllers ??
      Array.from(
        { length: carCount },
        (_, i): AgentController => (i === 0 ? { kind: 'learner' } : { kind: 'idle' })
      );
    if (controllers.length !== carCount) {
      throw new RangeError(`${controllers.length} controllers for ${carCount} cars`);
    }
    this.controllers = controllers;

    const agents: number[] = [];
    const scripted: number[] = [];
    for (let i = 0; i < controllers.length; i++) {
      if (controllers[i].kind === 'learner') agents.push(i);
      else if (controllers[i].kind === 'scripted') scripted.push(i);
    }
    if (agents.length === 0) throw new RangeError('an environment needs at least one learner');
    this.agentCarIndices = agents;
    this.scriptedIndices = scripted;
    this.referenceTier = options.referenceTier ?? null;

    this.frameSkip = Math.max(1, Math.floor(options.frameSkip ?? DEFAULT_FRAME_SKIP));
    this.episodeTicks = Math.max(1, Math.floor(options.episodeTicks ?? DEFAULT_EPISODE_TICKS));
    this.resetMode = options.resetMode ?? 'mixed';
    this.difficultyValue = clamp01(options.difficulty ?? 1);
    this.reward = new RewardFunction(options.rewardWeights);

    this.view = viewOf(this.world);
    this.pristineMatch = this.world.match.snapshot();
    this.carIndexOf = new Map(this.world.cars.map((car, i) => [car, i]));

    this.inputs = this.world.cars.map(() => neutralInput());
    this.window = {
      ticks: 0,
      goals: [],
      demolitions: [],
      touched: this.world.cars.map(() => false),
      boostGained: this.world.cars.map(() => 0),
      lastTouch: null,
    };
    this.snapshot = {
      ballPosition: vec3(),
      ballVelocity: vec3(),
      cars: this.world.cars.map(() => ({
        position: vec3(),
        velocity: vec3(),
        boostAmount: 0,
        demolished: false,
        touchedBall: false,
      })),
    };
    this.previousTouched = this.world.cars.map(() => false);
  }

  /** Number of learning agents. */
  get agentCount(): number {
    return this.agentCarIndices.length;
  }

  /** Ticks elapsed in the current episode. */
  get episodeTick(): number {
    return this.episodeTickCount;
  }

  get done(): boolean {
    return this.finished;
  }

  /** Curriculum position in [0, 1]. Only `nearBall` and `curriculum` read it. */
  get difficulty(): number {
    return this.difficultyValue;
  }

  /**
   * Move the curriculum. Takes effect on the NEXT `reset()`, never mid-episode:
   * an episode's initial state is the thing the difficulty describes, so
   * changing it under a running episode would describe nothing.
   */
  setDifficulty(value: number): void {
    this.difficultyValue = clamp01(value);
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  /**
   * Start a new episode. Everything stochastic about it — the reset variant,
   * the placements, the scripted opponents' generators — derives from `seed`.
   */
  reset(seed = 0): Observation[] {
    this.episodeSeed = seed >>> 0;
    this.rng.state = this.episodeSeed;

    // The world's own generator (demolition respawn draws) gets a seed derived
    // from, but not equal to, the episode seed: sharing one stream would make
    // the placement draws and the respawn draws move together.
    this.world.tick = 0;
    this.world.rng.state = (this.episodeSeed ^ 0x9e3779b9) >>> 0;
    this.world.match.restore(this.pristineMatch);
    this.world.resetForKickoff();

    const variant = this.pickResetMode();
    if (variant === 'kickoff') this.placeKickoff();
    else if (variant === 'nearBall') this.placeNearBall();
    else this.placeRandom();

    this.buildDirectors();

    this.episodeTickCount = 0;
    this.finished = false;
    this.refInputs = null;
    this.refInputsTick = -1;
    for (let i = 0; i < this.previousTouched.length; i++) this.previousTouched[i] = false;
    for (let i = 0; i < this.inputs.length; i++) this.inputs[i] = this.idleInput;

    return this.observations();
  }

  /** Fresh observations for every learning agent, at the current state. */
  observations(): Observation[] {
    return this.agentCarIndices.map((carIndex) =>
      buildObservation(this.world, carIndex, makeObservation())
    );
  }

  /**
   * Which concrete placement this episode gets.
   *
   * `curriculum` draws `nearBall` with probability (1 - difficulty) and falls
   * through to `mixed` otherwise, so the two distributions are BLENDED rather
   * than switched between: at difficulty 0.5 half the batch is still easy, and
   * a policy that has just been promoted keeps seeing the states it can already
   * handle. A hard switch at a threshold empties the batch of everything the
   * policy knows in one iteration, and the trust region does not survive it.
   */
  private pickResetMode(): 'kickoff' | 'random' | 'nearBall' {
    if (this.resetMode === 'kickoff') return 'kickoff';
    if (this.resetMode === 'random') return 'random';
    if (this.resetMode === 'nearBall') return 'nearBall';
    if (this.resetMode === 'curriculum' && this.rng.next() >= this.difficultyValue) {
      return 'nearBall';
    }
    return this.rng.next() < 0.5 ? 'kickoff' : 'random';
  }

  /**
   * A standard kickoff, but with the five spawns dealt out at random rather
   * than by the fixed formation. Every kickoff position is a genuinely
   * different problem — the far-back centre is a straight sprint, the corners
   * are a diagonal with a wall behind you — and training on only one of them
   * produces a policy that kicks off well and nothing else.
   */
  private placeKickoff(): void {
    const boost = this.world.physics.boostSpawnAmount;
    for (const team of [0, 1] as const) {
      const spawns = KICKOFF_SPAWNS[team];
      const order = shuffledIndices(this.rng, spawns.length);
      let n = 0;
      for (const car of this.world.cars) {
        if (car.team !== team) continue;
        resetCarToSpawn(car, spawns[order[n % order.length]], boost);
        n++;
      }
    }
  }

  /**
   * A random legal state: ball anywhere in play with a plausible velocity and
   * spin, cars on the floor facing anywhere, rolling, with a random tank.
   *
   * Cars are placed on the GROUND rather than in the air. An airborne car needs
   * a consistent orientation, angular velocity and jump/flip state to be a
   * state the simulation could actually have produced, and a car dropped into
   * the air with `hasFlip` true and `onGround` true is not one — it is a state
   * the policy will never see again after the first tick, so training on it is
   * training on noise.
   */
  private placeRandom(): void {
    const rng = this.rng;
    const ball = this.world.ball;

    const ballXY = samplePlanar(rng, RANDOM_BALL_HALF_X, RANDOM_BALL_HALF_Y);
    ball.position = vec3(
      ballXY.x,
      ballXY.y,
      rng.nextRange(this.world.physics.ballRestZ, RANDOM_BALL_MAX_Z)
    );
    ball.velocity = vec3(
      rng.nextRange(-RANDOM_BALL_MAX_SPEED, RANDOM_BALL_MAX_SPEED),
      rng.nextRange(-RANDOM_BALL_MAX_SPEED, RANDOM_BALL_MAX_SPEED),
      rng.nextRange(-RANDOM_BALL_MAX_SPEED, RANDOM_BALL_MAX_SPEED)
    );
    ball.angularVelocity = vec3(
      rng.nextRange(-RANDOM_BALL_MAX_SPIN, RANDOM_BALL_MAX_SPIN),
      rng.nextRange(-RANDOM_BALL_MAX_SPIN, RANDOM_BALL_MAX_SPIN),
      rng.nextRange(-RANDOM_BALL_MAX_SPIN, RANDOM_BALL_MAX_SPIN)
    );

    const taken: { x: number; y: number }[] = [{ x: ball.position.x, y: ball.position.y }];
    for (const car of this.world.cars) {
      let point = samplePlanar(rng, RANDOM_CAR_HALF_X, RANDOM_CAR_HALF_Y);
      for (let attempt = 0; attempt < SAMPLE_ATTEMPTS && tooClose(point, taken); attempt++) {
        point = samplePlanar(rng, RANDOM_CAR_HALF_X, RANDOM_CAR_HALF_Y);
      }
      taken.push(point);

      const spawn: Spawn = {
        position: vec3(point.x, point.y, CAR_SPAWN_REST_Z),
        yaw: rng.nextRange(-Math.PI, Math.PI),
        name: 'random',
      };
      resetCarToSpawn(car, spawn, rng.nextRange(0, BOOST_MAX));

      // Rolling along the car's own forward axis (column 0 of the orientation).
      const speed = rng.nextRange(RANDOM_CAR_MIN_SPEED, RANDOM_CAR_MAX_SPEED);
      const o = car.orientation;
      car.velocity = vec3(o[0] * speed, o[3] * speed, o[6] * speed);
    }
  }

  /**
   * The curriculum's placement: the ball somewhere plausible, and every LEARNER
   * on an arc around it whose radius, side, heading error and speed all grow
   * with the difficulty — the attacker behind the ball, the defender goalside of
   * it. Non-learners are placed as `placeRandom` would place them — a scripted
   * opponent handed the same head start would simply take the ball, which is not
   * the lesson.
   *
   * At difficulty 1 this is deliberately close to, but not identical with,
   * `placeRandom`: the learner's ring is 3500 uu and its heading is uniform, so
   * the only surviving difference is that the learner is placed relative to the
   * ball rather than independently of it. `curriculum` stops drawing this
   * variant at difficulty 1 anyway.
   */
  private placeNearBall(): void {
    const rng = this.rng;
    const d = this.difficultyValue;
    const ball = this.world.ball;
    const restZ = this.world.physics.ballRestZ;

    // Which team gets the chance this episode. Drawn 50/50 so the distribution
    // is symmetric across episodes even though a single one is not: whoever is
    // not attacking is spawned goalside of the ball, defending the net the
    // attacker is aimed at — the other half of a game this policy has never
    // seen.
    const attacking: 0 | 1 = rng.nextInt(2) === 0 ? 0 : 1;
    const attackSign = attacking === 0 ? 1 : -1;

    const fraction = lerp(NEAR_BALL_MIN_FIELD_FRACTION, 1, d);
    const ballXY = samplePlanar(rng, RANDOM_BALL_HALF_X * fraction, RANDOM_BALL_HALF_Y * fraction);
    // Drawn unconditionally, not inside a branch, so that the number of values
    // taken from the generator does not depend on the difficulty — a reset has
    // to stay a pure function of (seed, difficulty), and a difficulty-dependent
    // draw count would make two positions on the ramp share a seed and diverge.
    const shootingX = rng.nextRange(-NEAR_BALL_SHOOTING_HALF_X, NEAR_BALL_SHOOTING_HALF_X);
    ball.position = vec3(
      lerp(shootingX, ballXY.x, d),
      lerp(attackSign * NEAR_BALL_SHOOTING_Y, ballXY.y, d),
      lerp(restZ, RANDOM_BALL_MAX_Z, d * rng.next())
    );
    // Every one of these collapses to zero at difficulty 0, which is the point:
    // a stationary ball is the only target an untrained policy can reach twice.
    const speed = RANDOM_BALL_MAX_SPEED * d;
    ball.velocity = vec3(
      rng.nextRange(-speed, speed),
      rng.nextRange(-speed, speed),
      rng.nextRange(-speed, speed)
    );
    const spin = RANDOM_BALL_MAX_SPIN * d;
    ball.angularVelocity = vec3(
      rng.nextRange(-spin, spin),
      rng.nextRange(-spin, spin),
      rng.nextRange(-spin, spin)
    );

    const radius = lerp(NEAR_BALL_MIN_RADIUS, NEAR_BALL_MAX_RADIUS, d);
    const yawError = lerp(NEAR_BALL_MIN_YAW_ERROR, NEAR_BALL_MAX_YAW_ERROR, d);
    const approachError = lerp(NEAR_BALL_MIN_APPROACH_ERROR, NEAR_BALL_MAX_APPROACH_ERROR, d);
    // Out of the target goal, through the ball, and onward: the bearing the
    // attacker spawns on so that the ball lies between it and the goal. The
    // defender's own net IS that target goal, so its goalside bearing is the
    // same line walked the other way.
    const target = GOAL_CENTER[attacking === 0 ? 1 : 0];
    const behind = Math.atan2(ball.position.y - target.y, ball.position.x - target.x);
    const goalside = behind + Math.PI;
    const boost = this.world.physics.boostSpawnAmount;
    const learners = new Set(this.agentCarIndices);
    const taken: { x: number; y: number }[] = [{ x: ball.position.x, y: ball.position.y }];

    for (let i = 0; i < this.world.cars.length; i++) {
      const car = this.world.cars[i];
      let point: { x: number; y: number };
      if (learners.has(i)) {
        // Both teams are aimed: the attacker behind the ball, the defender
        // goalside of it. See `NEAR_BALL_MIN_APPROACH_ERROR`.
        //
        // At difficulty 1 `approachError` is pi, so `sampleRing` draws the whole
        // circle and the bearing selects nothing — every value of it gives the
        // same distribution. The defender's collapses back to the zero this line
        // passed before it was aimed, because "the same distribution" is not the
        // same DRAW: `angle` feeds cos, sin and the rejection test, so a rotated
        // arc centre moves every number in the placement. That is what keeps the
        // whole d=1 end of the ramp bit-identical to what it was, defender
        // included, rather than merely equal in distribution.
        const spread = approachError;
        const bearing =
          car.team === attacking ? behind : spread >= Math.PI ? 0 : goalside;
        point = this.sampleRing(rng, ball.position, NEAR_BALL_MIN_RADIUS, radius, taken, bearing, spread);
      } else {
        point = samplePlanar(rng, RANDOM_CAR_HALF_X, RANDOM_CAR_HALF_Y);
        for (let attempt = 0; attempt < SAMPLE_ATTEMPTS && tooClose(point, taken); attempt++) {
          point = samplePlanar(rng, RANDOM_CAR_HALF_X, RANDOM_CAR_HALF_Y);
        }
      }
      taken.push(point);

      // Pointing at the ball, plus an error that reaches "uniformly random" at
      // difficulty 1. Facing matters more than distance early on: a car spawned
      // beside the ball but pointing away has to learn to turn first, and
      // turning is a longer credit-assignment chain than driving forward.
      const toBall = Math.atan2(ball.position.y - point.y, ball.position.x - point.x);
      const yaw = learners.has(i)
        ? toBall + rng.nextRange(-yawError, yawError)
        : rng.nextRange(-Math.PI, Math.PI);
      const spawn: Spawn = {
        position: vec3(point.x, point.y, CAR_SPAWN_REST_Z),
        yaw,
        name: 'nearBall',
      };
      resetCarToSpawn(car, spawn, learners.has(i) ? boost : rng.nextRange(0, BOOST_MAX));

      const rolling = rng.nextRange(0, RANDOM_CAR_MAX_SPEED) * d;
      const o = car.orientation;
      car.velocity = vec3(o[0] * rolling, o[3] * rolling, o[6] * rolling);
    }
  }

  /**
   * A legal floor point on a ring around `center`, clear of everything already
   * placed. Falls back to the widest legal draw rather than looping.
   *
   * `bearing` and `spread` restrict which side of the ring is drawn from:
   * `spread` of pi is the whole ring and ignores `bearing`, which is what every
   * caller wanted before the curriculum needed to put an attacker behind the
   * ball and a defender goalside of it. The draw count is one per attempt either
   * way, so narrowing the arc
   * does not change how much of the generator a reset consumes.
   */
  private sampleRing(
    rng: Rng,
    center: { x: number; y: number },
    minRadius: number,
    maxRadius: number,
    taken: readonly { x: number; y: number }[],
    bearing = 0,
    spread = Math.PI
  ): { x: number; y: number } {
    const limit = ARENA.cornerPlaneIntercept - CORNER_MARGIN;
    for (let attempt = 0; attempt < SAMPLE_ATTEMPTS; attempt++) {
      const angle = bearing + rng.nextRange(-spread, spread);
      const distance = rng.nextRange(minRadius, Math.max(minRadius, maxRadius));
      const point = {
        x: center.x + Math.cos(angle) * distance,
        y: center.y + Math.sin(angle) * distance,
      };
      if (Math.abs(point.x) > RANDOM_CAR_HALF_X || Math.abs(point.y) > RANDOM_CAR_HALF_Y) continue;
      if (Math.abs(point.x) + Math.abs(point.y) > limit) continue;
      if (tooClose(point, taken)) continue;
      return point;
    }
    return samplePlanar(rng, RANDOM_CAR_HALF_X, RANDOM_CAR_HALF_Y);
  }

  /**
   * Rebuild the scripted directors for a new episode.
   *
   * Rebuilt rather than `reset()` so that each episode's opponents get their
   * own seeds: `BotDirector.reset` deliberately rewinds every bot to its
   * CONFIGURED seed, which would make every episode in a run face the identical
   * opponent and reward whatever exploits it.
   */
  private buildDirectors(): void {
    const seedFor = (carIndex: number): number =>
      (Math.imul(this.episodeSeed, 2654435761) ^ Math.imul(carIndex + 1, 40503)) >>> 0;

    if (this.scriptedIndices.length > 0) {
      const configs: BotConfig[] = this.scriptedIndices.map((carIndex) => {
        const controller = this.controllers[carIndex];
        const tier = controller.kind === 'scripted' ? controller.tier : 'allstar';
        const fixed = controller.kind === 'scripted' ? controller.seed : undefined;
        return { carIndex, difficulty: tier, seed: fixed ?? seedFor(carIndex) };
      });
      this.director = new BotDirector(this.world.arena, configs, { physics: this.world.physics });
    }

    if (this.referenceTier !== null) {
      const configs: BotConfig[] = this.agentCarIndices.map((carIndex) => ({
        carIndex,
        difficulty: this.referenceTier as ScriptedTier,
        seed: seedFor(carIndex + 97),
      }));
      this.refDirector = new BotDirector(this.world.arena, configs, {
        physics: this.world.physics,
      });
    }
  }

  // -------------------------------------------------------------------------
  // Step
  // -------------------------------------------------------------------------

  /**
   * Advance one action-repeat window. `actions` is parallel to
   * `agentCarIndices`.
   */
  step(actions: readonly ActionId[]): StepResult {
    if (this.finished) {
      throw new Error('step() on a finished episode; call reset() first');
    }
    if (actions.length !== this.agentCarIndices.length) {
      throw new RangeError(
        `${actions.length} actions for ${this.agentCarIndices.length} agents`
      );
    }

    this.captureSnapshot();
    this.beginWindow();

    // Chosen ONCE. The same frozen input object is handed to every tick of the
    // window; nothing below reassigns a learner's slot.
    for (let a = 0; a < this.agentCarIndices.length; a++) {
      this.inputs[this.agentCarIndices[a]] = actionInput(actions[a]);
    }
    for (let i = 0; i < this.inputs.length; i++) {
      if (this.controllers[i].kind === 'idle') this.inputs[i] = this.idleInput;
    }

    let terminated: EndReason | null = null;
    let ticks = 0;
    for (let t = 0; t < this.frameSkip; t++) {
      this.applyScriptedInputs();
      // Kept in step with the world even when the caller never asks for it, so
      // the reference bot's prediction cache and kickoff timer stay honest.
      if (this.refDirector !== null) this.referenceInputs();

      const events = this.world.step(this.inputs);
      ticks++;
      this.episodeTickCount++;

      this.director?.observe(events);
      this.refDirector?.observe(events);
      this.accumulate(events);

      if (events.goal !== null) {
        terminated = 'goal';
        break;
      }
      if (this.episodeTickCount >= this.episodeTicks) {
        terminated = 'timeout';
        break;
      }
    }
    this.window.ticks = ticks;
    // Read once, at the end of the window: whoever the ball is still obeying is
    // the car `ballToOpponentGoalDistance` credits. The world clears this on a
    // kickoff reset, so it is null exactly while no touch has happened yet.
    this.window.lastTouch =
      this.world.lastTouch === null ? null : this.world.lastTouch.carIndex;
    this.finished = terminated !== null;

    // --- score ---------------------------------------------------------------
    const observations: Observation[] = [];
    const rewards: number[] = [];
    const rewardTerms: Record<string, number>[] = [];
    const goalsFor: number[] = [];
    const goalsAgainst: number[] = [];
    const touched: boolean[] = [];

    for (const carIndex of this.agentCarIndices) {
      const breakdown = emptyRewardBreakdown();
      const context: RlRewardContext = {
        carIndex,
        world: this.world,
        events: this.window,
        previous: this.snapshot,
        episodeTick: this.episodeTickCount,
      };
      rewards.push(this.reward.evaluate(context, breakdown));
      rewardTerms.push(breakdown);
      // A fresh buffer per step: a rollout buffer holds these across steps, and
      // a shared array would silently rewrite every stored observation.
      observations.push(buildObservation(this.world, carIndex, makeObservation()));

      const team = this.world.cars[carIndex].team;
      let scored = 0;
      let conceded = 0;
      for (const goal of this.window.goals) {
        if (goal.team === team) scored++;
        else conceded++;
      }
      goalsFor.push(scored);
      goalsAgainst.push(conceded);
      touched.push(this.window.touched[carIndex]);
    }

    for (let i = 0; i < this.previousTouched.length; i++) {
      this.previousTouched[i] = this.window.touched[i];
    }

    return {
      observations,
      rewards,
      done: this.finished,
      info: {
        episodeTick: this.episodeTickCount,
        ticks,
        terminated,
        goalsFor,
        goalsAgainst,
        touched,
        rewardTerms,
      },
    };
  }

  /**
   * What the scripted reference bot would do right now, as action indices,
   * parallel to `agentCarIndices`. Requires `referenceTier`.
   */
  referenceActions(): ActionId[] {
    const inputs = this.referenceInputs();
    return inputs.map(nearestAction);
  }

  // -------------------------------------------------------------------------

  private applyScriptedInputs(): void {
    if (this.director === null) return;
    const produced = this.director.step(this.view);
    for (let i = 0; i < this.scriptedIndices.length; i++) {
      this.inputs[this.scriptedIndices[i]] = produced[i];
    }
  }

  /**
   * The reference director stepped exactly ONCE per world tick, cached on the
   * tick index. `referenceActions()` and the step loop both call this, and a
   * bot run twice on the same tick would advance its kickoff timer and its
   * reaction queue at double rate.
   */
  private referenceInputs(): CarInput[] {
    if (this.refDirector === null) {
      throw new Error('referenceActions() requires EnvOptions.referenceTier');
    }
    if (this.refInputs === null || this.refInputsTick !== this.world.tick) {
      this.refInputs = this.refDirector.step(this.view);
      this.refInputsTick = this.world.tick;
    }
    return this.refInputs;
  }

  private beginWindow(): void {
    this.window.ticks = 0;
    this.window.goals.length = 0;
    this.window.demolitions.length = 0;
    for (let i = 0; i < this.window.touched.length; i++) {
      this.window.touched[i] = false;
      this.window.boostGained[i] = 0;
    }
  }

  private captureSnapshot(): void {
    const ball = this.world.ball;
    this.snapshot.ballPosition = clone(ball.position);
    this.snapshot.ballVelocity = clone(ball.velocity);
    for (let i = 0; i < this.world.cars.length; i++) {
      const car = this.world.cars[i];
      const slot = this.snapshot.cars[i];
      slot.position = clone(car.position);
      slot.velocity = clone(car.velocity);
      slot.boostAmount = car.boostAmount;
      slot.demolished = car.demolished;
      slot.touchedBall = this.previousTouched[i];
    }
  }

  private accumulate(events: WorldStepEvents): void {
    for (const touch of events.ballTouches) this.window.touched[touch.carIndex] = true;

    for (const pickup of events.boostPickups) {
      const index = this.carIndexOf.get(pickup.car);
      if (index !== undefined) this.window.boostGained[index] += pickup.amount;
    }

    for (const demo of events.demolitions) {
      const attacker = this.carIndexOf.get(demo.attacker);
      const victim = this.carIndexOf.get(demo.victim);
      if (attacker !== undefined && victim !== undefined) {
        this.window.demolitions.push({ attacker, victim });
      }
    }

    if (events.goal !== null) {
      // `lastTouch` is read here, one call after the goal was detected and
      // before any kickoff reset clears it — it is the only record of WHO put
      // the ball in, which `GoalEvent.scorer` deliberately nulls for own goals.
      this.window.goals.push({
        team: events.goal.team,
        scorer: events.goal.scorer,
        lastTouch: this.world.lastTouch === null ? null : this.world.lastTouch.carIndex,
      });
    }
  }
}

/** Difficulty is a fraction; anything outside [0, 1] is a caller's mistake and
 *  is clamped rather than allowed to widen the placement envelope. */
const clamp01 = (value: number): number =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;

/** Is `point` inside `MIN_SEPARATION` of anything already placed? */
const tooClose = (point: { x: number; y: number }, taken: readonly { x: number; y: number }[]): boolean => {
  for (const other of taken) {
    if (Math.hypot(point.x - other.x, point.y - other.y) < MIN_SEPARATION) return true;
  }
  return false;
};

/**
 * A uniformly random action, from a seeded generator. The baseline the reward
 * function has to beat: if a competent player does not out-score this, the
 * reward function is pointing somewhere other than at playing well.
 */
export const randomAction = (rng: Rng, actionCount: number): ActionId =>
  rng.nextInt(actionCount);
